import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  createTable,
  getCoreRowModelSync,
  useTableInstance,
} from '@tanstack/react-table';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import {
  AutoCompleteInput,
  AutoCompleteItem,
  Paginator,
} from '@nowsta/tempo-ds';

import EmptyState from 'features/common/components/EmptyState';
import { QueryKeys } from 'features/common/constants';
import { fetchAgenciesRequest } from 'features/agencies/hooks/queries';
import { useURLQueryParams } from 'features/common/hooks/useURLQueryParams';
import { fetchAllReceivables, fetchPunchcardReceivables } from 'services';

import EditEntityDrawer from 'features/invoices/components/EditInvoiceDrawer';
import LoadingState from '../LoadingState';

import { HEADER_COLUMNS, getReceivableData } from './helpers';
import { Filters } from './types';

import * as Presenter from './Presenter';

export const ReceivablesListView = (
  props: React.ComponentPropsWithRef<'div'>,
) => {
  const { urlParams, setURLParams } = useURLQueryParams();
  const [editReceivableId, setEditReceivableId] = useState<number | null>(null);

  const { agencyFilters } = urlParams.all();

  const filters = useMemo(
    () => JSON.parse(agencyFilters || '{"page": 1, "limit": 10}'),
    [agencyFilters],
  );

  const methods = useForm<Filters>({
    defaultValues: {
      companyId: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const formData = useWatch({ control: methods.control });

  useEffect(() => {
    methods.reset({ ...methods.getValues(), ...filters });
  }, [methods.reset, filters]);

  const agenciesQuery = useQuery(
    [QueryKeys.agencies, { offset: 1, limit: 0, search: '' }],
    () => fetchAgenciesRequest(1, 0, ''),
    {
      select: queryData => queryData?.data.items,
    },
  );

  const receivablesQuery = useQuery(
    [
      QueryKeys.agencyReceivables,
      { companyId: formData.companyId || undefined, ...filters },
    ],
    () => {
      if (!formData.companyId) {
        return fetchAllReceivables({ params: filters });
      }
      return fetchPunchcardReceivables({
        params: { companyId: formData.companyId || undefined, ...filters },
      });
    },
    {
      enabled: methods.formState.isValid,
    },
  );

  const handlePaginationChange = (param: 'limit' | 'page') => (value: number) =>
    setURLParams(prev => ({
      ...prev,
      agencyFilters: JSON.stringify({
        ...filters,
        [param]: value,
      }),
    }));

  const handleSelectedCompany =
    (onChange: (event: any) => void) =>
    (selection: AutoCompleteItem<object> | null) => {
      onChange(selection?.id);
      setURLParams(prev => ({
        ...prev,
        agencyFilters: JSON.stringify({
          ...filters,
          companyId: selection?.id,
        }),
      }));
    };

  const table = createTable();

  const columnsPlaceholder = useMemo(() => HEADER_COLUMNS, []);

  const receivableEdit = useMemo(() => {
    if (editReceivableId) {
      return receivablesQuery.data?.items.find(
        receivable => receivable.id === editReceivableId,
      );
    }
  }, [editReceivableId, receivablesQuery.data]);

  const handleCloseEditReceivable = () => setEditReceivableId(null);
  const handleSuccessEditReceivable = () => {
    receivablesQuery.refetch();
  };

  const data = useMemo(
    () =>
      receivablesQuery.data?.items.map(item =>
        getReceivableData(item, { onEdit: setEditReceivableId }),
      ) ?? [],
    [receivablesQuery.data?.items],
  );

  const columns = table.createColumns(columnsPlaceholder);

  const instance = useTableInstance(table, {
    data,
    columns,
    getCoreRowModel: getCoreRowModelSync(),
  });

  const isLoadingReceivables =
    receivablesQuery.isLoading && receivablesQuery.fetchStatus !== 'idle';
  const selectedCompany = useMemo(
    () =>
      agenciesQuery.data?.find(
        company => company.companyId === filters.companyId,
      ),
    [filters.companyId, agenciesQuery.data],
  );
  const renderContent = () => {
    if (isLoadingReceivables) return <LoadingState />;

    if (!receivablesQuery.data?.items.length) {
      const baseMessage = 'No receivables found for';
      const complement = selectedCompany
        ? `agency: ${selectedCompany?.name}.`
        : 'the criteria provided.';

      const message = `${baseMessage} ${complement}`;
      return <EmptyState message={message} />;
    }

    return (
      <Presenter.TableContainer>
        <Presenter.Table {...instance.getTableProps()}>
          <Presenter.TableHeader>
            {instance.getHeaderGroups().map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>{column.renderHeader()}</th>
                ))}
              </tr>
            ))}
          </Presenter.TableHeader>
          <Presenter.TableBody {...instance.getTableBodyProps()}>
            {instance.getRowModel().rows.map(row => (
              <tr {...row.getRowProps()}>
                {row.getVisibleCells().map(cell => (
                  <td role="presentation" {...cell.getCellProps()}>
                    <Presenter.Cell>{cell.renderCell()}</Presenter.Cell>
                  </td>
                ))}
              </tr>
            ))}
          </Presenter.TableBody>
        </Presenter.Table>
      </Presenter.TableContainer>
    );
  };

  return (
    <div {...props}>
      <Presenter.Header>Receivables</Presenter.Header>
      <FormProvider {...methods}>
        <Controller
          name="companyId"
          render={({ field }) => (
            <AutoCompleteInput
              label="Agency"
              openOnFocus
              palette="white"
              inputValue={selectedCompany?.name || ''}
              onSelected={handleSelectedCompany(field.onChange)}
              placeholder="Company"
              {...field}
              items={
                agenciesQuery.data?.map(company => ({
                  id: String(company.companyId),
                  displayValue: company.name,
                })) ?? []
              }
            />
          )}
        />
      </FormProvider>
      {renderContent()}

      {!isLoadingReceivables && (
        <Paginator
          palette="white"
          uiSize="small"
          limitOptions={[
            10,
            25,
            50,
            100,
            receivablesQuery.data?.meta?.totalItems ?? 0,
          ]}
          isWorking={isLoadingReceivables}
          spaceUI={{ pT: 12, pB: 16 }}
          pageNumber={filters.page}
          limit={filters.limit}
          onLimitChange={handlePaginationChange('limit')}
          onPageNumberChange={handlePaginationChange('page')}
          totalPages={receivablesQuery?.data?.meta?.totalPages ?? 0}
        />
      )}
      {receivableEdit && (
        <EditEntityDrawer
          entity={receivableEdit!}
          isInvoice={false}
          visible={Boolean(editReceivableId)}
          onClose={handleCloseEditReceivable}
          onSuccess={handleSuccessEditReceivable}
        />
      )}
    </div>
  );
};
